/**
 * OrganizationsUsersComponent
 * 
 * This component manages the assignment and removal of users from organizations.
 * It provides functionality for:
 * - Viewing users assigned to an organization.
 * - Viewing users available for assignment.
 * - Assigning users to a selected organization.
 * - Removing users from an organization.
 * - Form validation to ensure proper input.
 * 
 * Author: Prajna
 * Date: 2024-12-10
 */

import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { OrganizationService } from '../organization.service';
import { UserService } from 'src/app/services/user.service';
import { MatTableDataSource } from '@angular/material/table';
import { User } from 'src/app/services/user';
import { BaseComponent } from 'src/app/base/base.component';
import { ErrorReportingService } from 'src/app/services/errorReporting/error-reporting.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CreateUserDialogComponent } from '../../create-user-dialog/create-user-dialog.component';
import { MatDialogConfig } from '@angular/material/dialog';
import { CreateAdminDialogComponent } from '../../create-admin-dialog/create-admin-dialog.component';

@Component({
    selector: 'app-organizations-users',
    templateUrl: './organizations-users.component.html',
    styleUrls: ['./organizations-users.component.css'],
    standalone: false
})
export class OrganizationsUsersComponent  extends BaseComponent implements OnInit{
  // Data source for MatTable to display assigned users
  users = new MatTableDataSource<User>();
  hasError: boolean = false; // Tracks error state
  displayedColumns: string[] = ['userName', 'role', 'email', 'actions']; // Columns to display in the table
  defaultOrganizationId: string = '0'; // ID representing the default organization
  organizationId: string = '1'; // ID of the selected organization
  organizationName: string = ''; // ID of the selected organization
  availableUsers: User[] = []; // Users available for assignment
  assignedUsers: User[] = []; // Users already assigned to the selected organization
  assignUsersForm: FormGroup; // Form group for user assignment

  // List of user types and their corresponding roles
  userTypes = [
    { id: 2, label: 'Admin' },
    { id: 3, label: 'Analyst' },
    { id: 4, label: 'Operator' }
  ];

  /**
   * Constructor to initialize dependencies and form.
   * @param data Injected data for dialog
   * @param organizationService Service for organization-related API calls
   * @param userService Service for user-related API calls
   * @param fb FormBuilder for creating reactive forms
   * @param dialogRef Reference to the current dialog
   */
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private userService: UserService,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<OrganizationsUsersComponent>,
    errorLogger: ErrorReportingService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    super(errorLogger, dialog);
    this.organizationId=data.orgnizationId
    this.organizationName=data.orgnizationName
   // console.log("hhhh==", this.organizationName);
    // Initialize the form with default values and validation
    this.assignUsersForm = this.fb.group({
      selectedUsers: [[], Validators.required], // Users to assign
      selectedRole: [null, Validators.required], // Role of users
    });
  }

  /**
   * Lifecycle hook to load data when the component initializes.
   * Fetches assigned and available users for the selected organization.
   */
  ngOnInit(): void {
    this.fetchUsers(this.organizationId, 'assigned'); // Fetch assigned users
    this.fetchUsers(this.defaultOrganizationId, 'available'); // Fetch available users
    //console.log("org name==",this.organizationName);
  }

  /**
   * Returns the role name corresponding to a user type ID.
   * @param userType User type ID
   * @returns Role name
   */
  getRole(userType: number): string {
    switch (userType) {
      case 2: return 'Admin';
      case 3: return 'Analyst';
      case 4: return 'Operator';
      default: return 'Analyst'; // Default role
    }
  }

  /**
   * Remove a user from the selected organization.
   * @param id  ID to remove
   */

  removeUser(id: string): void {
    // Replace the confirm with a custom confirmation dialog
    this.showConfirmDialog(
      'Delete User?', // Title of the dialog
      'Are you sure you want to remove this user from the Organization?', // Message of the dialog
      async (result) => {
        if (result) { // Proceed only if the user confirms (result is true)
          // Call the service to delete the user by their ID
          this.userService.deleteUserOrganization(id).subscribe(
            async response => {
            //  console.log('User removed successfully:', response);
              this.snackBar.open('User removed successfully:', 'Close', {
                duration: 3000, // Duration in milliseconds
              });
              await this.fetchUsers(this.organizationId, 'assigned'); // Refresh the assigned user list
              await this.fetchUsers(this.organizationId, 'available'); // Refresh the assigned user list
            },
            error => {
              console.error('Error removing user:', error);
            }
          );
        }
      }
    );
  }
  /**
   * Assigns selected users to the organization.
   * Validates the form and iterates through the selected users to assign them individually.
   * After successful assignment, refreshes the user list and resets the form.
   */
  async assignUsers(): Promise<void> {
    if (this.assignUsersForm.valid) {
      const selectedUsers = this.assignUsersForm.value.selectedUsers.filter(id => id != null);
     // console.log("User count:", selectedUsers.length);

      if (selectedUsers.length > 0) {
        for (const userId of selectedUsers) {
          const payload = { userId, organizationId: this.organizationId,roleId: this.assignUsersForm.value.selectedRole };
        //  console.log("Payload:", payload);

          try {
            const organizationAssignResponse = await this.userService.assignOrganization(payload).toPromise();
            console.log('API response:', organizationAssignResponse);
            this.snackBar.open('User successfully assigned', 'Close', {
              duration: 3000, // Duration in milliseconds
            });
          } catch (error) {
            console.error('Error assigning organization:', error);
          }
        }

        await this.fetchUsers(this.organizationId, 'assigned'); // Refresh assigned users
        await this.fetchUsers(this.organizationId, 'available'); // Refresh assigned users
        this.assignUsersForm.reset(); // Reset the form
        Object.keys(this.assignUsersForm.controls).forEach(field => {
          const control = this.assignUsersForm.get(field);
          control?.markAsUntouched();
          control?.markAsPristine();
          control?.setErrors(null); // Clear the errors
        });
        console.log('Users assigned successfully!');
      } else {
        console.error('No valid users selected.');
      }
    } else {
      console.error('Form is invalid');
    }
  }

  /**
   * Cancels the dialog and closes it.
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Fetches users from the server based on organization ID and type (available or assigned).
   * Updates the corresponding user list on success or sets an error state on failure.
   * @param organizationId ID of the organization
   * @param userType Type of users ('available' or 'assigned')
   */
  async fetchUsers(organizationId: string, userType: 'available' | 'assigned'): Promise<void> {
    try {
     
      if (userType === 'available') {
        const data = await this.userService.getUnAssignedUserList(this.organizationId);
        this.availableUsers = data; // Update available users
      } else if (userType === 'assigned') {
        const data = await this.userService.getAssignedUserList(this.organizationId);
        this.assignedUsers = data; // Update assigned users
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      this.hasError = true;
    }
  }
  OpenUserEntryDialog(): void {
    const dialogConfig = new MatDialogConfig();
    
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '600px'; // Adjust the width as needed
    dialogConfig.maxWidth = '90vw'; // Responsive max width
   
    dialogConfig.data = { title: 'Create Admin',
      organizationId: this.organizationId,
      organozationName:this.organizationName}; // Pass the title in the data object
    //console.log( "  dialogConfig.data =", dialogConfig.data )
    const dialogRef = this.matDialog.open(CreateAdminDialogComponent, dialogConfig);
  
    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        if (result.status === true) {
          await this.fetchUsers(this.organizationId, 'assigned'); // Refresh assigned users
          await this.fetchUsers(this.organizationId, 'available'); // Refresh assigned users
          // Show success message
          this.snackBar.open(result.message, 'Close', {
            duration: 3000, // Duration in milliseconds
          });
        //  this.getUsers(this.organizationId); // Refresh user list
        } else if (result.status === false) {
          // Show specific error message if provided
          const errorMessage = result.message || 'Error creating user. Please try again.'; // Use the provided message or a default one
          this.snackBar.open(errorMessage, 'Close', {
            duration: 3000, // Duration in milliseconds
          });
        }
      }
    });
    
    
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.name) {
        // Handle the result here
      }
    });
  }
}
